/**
 * THE LOSS FILE ON THE JOB CARD.
 *
 * Anthony, 2026-10-28, for Peter Banyai's RemediationX and Nevada Water
 * Damage: "build for water damage and what they do, custom version". Drawn
 * only for a shop whose trade is 'restoration' (words().lossFile), above
 * the tabs, because the claim and the drying log are what the office and
 * the adjuster ask about first.
 *
 * Four parts, each saved on its own button so a tech in a wet crawlspace
 * who types one reading does not lose it to somebody else's claim edit:
 *
 *   1. The loss: date of loss, category, class.
 *   2. The claim: carrier, adjuster, phone, email, policy, deductible.
 *      The claim number itself is the job's identity box (the serial), so
 *      the job can close on it, the same rule every other trade has.
 *   3. Equipment on site and the drying days, counted by calendar day the
 *      way equipment is billed.
 *   4. Moisture readings, newest last, with a dry goal per spot. A spot
 *      still above its goal is named, so nobody closes up a wet wall.
 *
 * Every save re-reads jobs.loss first and writes back the whole record
 * with only its own part changed, so two people on one job do not undo
 * each other.
 *
 * ─── IT SURVIVES THE MIGRATION NOT BEING THERE ──────────────────────────
 *
 * The card reads jobs.loss with its own select. Until 0235 is applied that
 * select errors and the card hides itself. In practice no account can be
 * on 'restoration' before 0235 widens the trade check anyway.
 */

import { db } from "../data/client.ts";
import type { JobRow } from "../data/jobs.ts";
import { esc } from "../ui/dom.ts";
import type { Role } from "../domain/permissions.ts";
import {
  CATEGORIES, CLASSES, EQUIPMENT, readLoss, dryingDays, wetSpots, lossLine,
  type LossRecord, type EquipmentKey,
} from "../domain/lossfile.ts";

/** jobs.loss for one job, or null when 0235 is not there yet. */
async function readRow(jobId: string): Promise<{ loss: unknown } | null> {
  try {
    const sb = await db();
    const { data, error } = await sb.from("jobs").select("loss").eq("id", jobId).maybeSingle();
    if (error || !data) return null;
    return data as { loss: unknown };
  } catch { return null; }
}

/** Write jobs.loss, and prove a row moved. Silence is not a save. */
async function writeLoss(jobId: string, loss: LossRecord): Promise<void> {
  const sb = await db();
  const { data, error } = await sb.from("jobs").update({ loss }).eq("id", jobId).select("id");
  if (error) throw new Error(error.message);
  if (!data || !data.length) throw new Error("That did not save. You may not have rights to change this job.");
}

const today = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export async function mountLossFile(hostId: string, job: JobRow, role: Role): Promise<void> {
  const host = document.getElementById(hostId);
  if (!host) return;
  const row = await readRow(job.id);
  if (!row) { host.hidden = true; return; }
  let rec = readLoss(row.loss);
  let note = "";
  const can = role !== "viewer" && job.status !== "cancelled";
  const dis = can ? "" : "disabled";

  const input = (attr: string, label: string, v: unknown, type = "text", extra = "") =>
    `<label class="field"><span>${label}</span><input ${attr} type="${type}" ${extra}
      value="${esc(v ?? "")}" ${dis}></label>`;
  const select = (attr: string, label: string, list: ReadonlyArray<{ key: string; label: string }>, on?: string) =>
    `<label class="field"><span>${label}</span><select ${attr} ${dis}>
      <option value="">Not set</option>
      ${list.map((o) => `<option value="${o.key}"${o.key === on ? " selected" : ""}>${esc(o.label)}</option>`).join("")}
    </select></label>`;

  const draw = () => {
    const days = dryingDays(rec, today());
    const wet = wetSpots(rec);
    const summary = lossLine(rec, today());
    const ins = rec.insurance;
    host.innerHTML = `
      <div class="card-head">The loss file</div>
      <div class="pad">
        ${summary ? `<p class="quiet">${esc(summary)}.</p>` : ""}
        ${wet.length ? `<p class="msg bad">Still wet: ${wet.map((r) => esc(`${r.where} ${r.value} (goal ${r.goal})`)).join(", ")}.</p>` : ""}
        <div class="msg-slot" data-lossmsg>${note}</div>

        <details class="lossbook" open>
          <summary>The loss</summary>
          <div class="lossgrid">
            ${input(`data-l="loss_date"`, "Date of loss", rec.loss_date, "date")}
            ${select(`data-l="category"`, "Category", CATEGORIES, rec.category)}
            ${select(`data-l="cls"`, "Class", CLASSES, rec.cls)}
          </div>
          ${can ? `<button class="btn sm" data-save="loss">Save the loss</button>` : ""}
        </details>

        <details class="lossbook"${ins.carrier || ins.adjuster ? " open" : ""}>
          <summary>Insurance claim</summary>
          <p class="fine">The claim number goes in the claim number box on the Loss tab, so the job can close on it.</p>
          <div class="lossgrid">
            ${input(`data-i="carrier"`, "Carrier", ins.carrier)}
            ${input(`data-i="policy"`, "Policy number", ins.policy)}
            ${input(`data-i="adjuster"`, "Adjuster", ins.adjuster)}
            ${input(`data-i="adjuster_phone"`, "Adjuster phone", ins.adjuster_phone, "tel")}
            ${input(`data-i="adjuster_email"`, "Adjuster email", ins.adjuster_email, "email")}
            ${input(`data-i="deductible"`, "Deductible", ins.deductible, "number", `inputmode="decimal" min="0" step="1"`)}
          </div>
          ${can ? `<button class="btn sm" data-save="claim">Save the claim</button>` : ""}
        </details>

        <details class="lossbook" open>
          <summary>Equipment and drying${days !== null ? `, ${rec.drying_end ? `dried in ${days} days` : `day ${days}`}` : ""}</summary>
          <div class="lossgrid">
            ${EQUIPMENT.map((e) => input(`data-e="${e.key}"`, e.label, rec.equipment[e.key] ?? "", "number",
              `inputmode="numeric" min="0" step="1"`)).join("")}
            ${input(`data-d="drying_start"`, "Drying started", rec.drying_start, "date")}
            ${input(`data-d="drying_end"`, "Equipment pulled", rec.drying_end, "date")}
          </div>
          ${can ? `<button class="btn sm" data-save="equipment">Save equipment</button>` : ""}
        </details>

        <details class="lossbook" open>
          <summary>Moisture readings (${rec.readings.length})</summary>
          ${rec.readings.length ? `<table><thead><tr><th>Day</th><th>Where</th><th>Reading</th><th>Goal</th></tr></thead><tbody>
            ${rec.readings.map((r) => `<tr><td>${esc(r.at)}</td><td>${esc(r.where)}</td><td>${esc(r.value)}</td><td>${esc(r.goal ?? "")}</td></tr>`).join("")}
          </tbody></table>` : `<p class="quiet">No readings yet.</p>`}
          ${can ? `<div class="lossgrid">
            ${input(`data-r="at"`, "Day", today(), "date")}
            ${input(`data-r="where"`, "Room and material", "", "text", `placeholder="Kitchen, drywall"`)}
            ${input(`data-r="value"`, "Reading", "", "number", `inputmode="decimal" step="0.1"`)}
            ${input(`data-r="goal"`, "Dry goal", "", "number", `inputmode="decimal" step="0.1"`)}
          </div>
          <button class="btn sm" data-save="reading">Add the reading</button>` : ""}
        </details>
      </div>`;

    const say = (t: string, bad = false) => {
      note = `<p class="msg${bad ? " bad" : " good"}">${esc(t)}</p>`;
      const m = host.querySelector<HTMLElement>("[data-lossmsg]");
      if (m) m.innerHTML = note;
    };
    const val = (sel: string) =>
      host.querySelector<HTMLInputElement | HTMLSelectElement>(sel)?.value.trim() ?? "";

    const save = async (part: string, b: HTMLButtonElement) => {
      /* Re-read first, so this part lands on whatever somebody else saved
         a minute ago rather than on what this screen loaded. */
      const fresh = readLoss((await readRow(job.id))?.loss ?? rec);
      let next: LossRecord = fresh;
      if (part === "loss") {
        next = { ...fresh, loss_date: val(`[data-l="loss_date"]`) || undefined,
          category: val(`[data-l="category"]`) || undefined, cls: val(`[data-l="cls"]`) || undefined };
      } else if (part === "claim") {
        const ded = val(`[data-i="deductible"]`);
        next = { ...fresh, insurance: {
          carrier: val(`[data-i="carrier"]`), policy: val(`[data-i="policy"]`),
          adjuster: val(`[data-i="adjuster"]`), adjuster_phone: val(`[data-i="adjuster_phone"]`),
          adjuster_email: val(`[data-i="adjuster_email"]`), deductible: ded === "" ? null : Number(ded) } };
      } else if (part === "equipment") {
        const equipment: Partial<Record<EquipmentKey, number>> = {};
        for (const e of EQUIPMENT) {
          const n = Number(val(`[data-e="${e.key}"]`));
          if (Number.isFinite(n) && n > 0) equipment[e.key] = Math.round(n);
        }
        next = { ...fresh, equipment, drying_start: val(`[data-d="drying_start"]`) || undefined,
          drying_end: val(`[data-d="drying_end"]`) || undefined };
      } else if (part === "reading") {
        const where = val(`[data-r="where"]`);
        const v = val(`[data-r="value"]`);
        const goal = val(`[data-r="goal"]`);
        if (!where || v === "" || !Number.isFinite(Number(v))) { say("Say where it was taken and what the meter read.", true); return; }
        next = { ...fresh, readings: [...fresh.readings,
          { at: val(`[data-r="at"]`) || today(), where, value: Number(v), goal: goal === "" ? null : Number(goal) }] };
      }
      next = readLoss(next);
      b.disabled = true;
      try {
        await writeLoss(job.id, next);
        rec = next;
        note = `<p class="msg good">Saved.</p>`;
        draw();
      } catch (e) {
        b.disabled = false;
        say(e instanceof Error ? e.message : String(e), true);
      }
    };
    host.querySelectorAll<HTMLButtonElement>("button[data-save]").forEach((b) =>
      b.addEventListener("click", () => void save(b.dataset["save"]!, b)));
  };
  draw();
}
